/**
 * Done. / ChiroCPD — ActivityRow Feature Component
 *
 * Collapsed by default showing summary row only.
 * Clicking the row expands it to reveal certificate section, reflection panel,
 * and (when isEditing) the inline edit form.
 */

import { useState, useCallback, useRef } from 'react';
import { Link } from 'wouter';
import { Button } from '@/components/ui/Button';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { AIReflectionPanel } from '@/components/features/AIReflectionPanel';
import { ActivityForm } from '@/components/features/ActivityForm';
import { formatDate, formatHours } from '@/lib/utils';
import { api } from '@/lib/api';
import type { Activity, ActivityType, CreateActivityInput, Reflection } from '@/lib/types';
import type { CertEntry } from '@/components/dashboard/MyCertificates';

interface ActivityRowProps {
  activity: Activity;
  completedModules?: CertEntry[];
  activityTypes?: ActivityType[];
  isEditing?: boolean;
  onEdit: (activity: Activity) => void;
  onSaveEdit: (id: string, input: CreateActivityInput) => Promise<void>;
  onCancelEdit: () => void;
  onDelete: (id: string) => Promise<void>;
  onReflectionSaved?: ((reflection: Reflection) => void) | undefined;
}

export function ActivityRow({
  activity,
  completedModules = [],
  activityTypes = [],
  isEditing = false,
  onEdit,
  onSaveEdit,
  onCancelEdit,
  onDelete,
  onReflectionSaved,
}: ActivityRowProps): React.ReactElement {
  const [expanded, setExpanded] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [hasReflection, setHasReflection] = useState(activity.hasReflection);
  const [certKey, setCertKey] = useState(activity.certificateKey ?? null);
  const [linkedModuleId, setLinkedModuleId] = useState(activity.linkedModuleId ?? null);
  const [uploading, setUploading] = useState(false);
  const [certError, setCertError] = useState<string | null>(null);
  const [showLinkPicker, setShowLinkPicker] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isOpen = expanded || isEditing;

  const linkedModule = linkedModuleId
    ? completedModules.find(m => m.moduleId === linkedModuleId)
    : null;

  const handleDelete = useCallback(async (): Promise<void> => {
    if (!window.confirm(`Delete "${activity.title}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await onDelete(activity.id);
    } catch {
      setDeleting(false);
    }
  }, [activity.id, activity.title, onDelete]);

  const handleCertUpload = useCallback(async (file: File): Promise<void> => {
    setCertError(null);
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const result = await api.upload<{ certificateKey: string }>(
        `/api/activities/${activity.id}/certificate`,
        form,
      );
      setCertKey(result.certificateKey);
    } catch (err) {
      setCertError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }, [activity.id]);

  const handleRemoveCert = useCallback(async (): Promise<void> => {
    if (!window.confirm('Remove the attached certificate?')) return;
    setCertError(null);
    try {
      await api.delete(`/api/activities/${activity.id}/certificate`);
      setCertKey(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      setCertError(err instanceof Error ? err.message : 'Failed to remove certificate');
    }
  }, [activity.id]);

  const handleLinkModule = useCallback(async (moduleId: string | null): Promise<void> => {
    setCertError(null);
    try {
      await api.put(`/api/activities/${activity.id}/link-module`, { moduleId });
      setLinkedModuleId(moduleId);
      setShowLinkPicker(false);
    } catch (err) {
      setCertError(err instanceof Error ? err.message : 'Failed to link module');
    }
  }, [activity.id]);

  const certUrl = `/api/activities/${activity.id}/certificate`;
  const hasCert = Boolean(certKey || linkedModuleId);

  return (
    <div className="border-b border-[var(--border-subtle)] last:border-0">
      {/* Summary row — always visible, click to expand */}
      <button
        type="button"
        onClick={() => { setExpanded(e => !e); }}
        className="w-full text-left flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 py-3 px-4 hover:bg-[var(--surface-subtle)] transition-colors"
        aria-expanded={isOpen}
      >
        {/* Chevron */}
        <span className={`material-symbols-outlined text-[18px] text-[var(--text-muted)] shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`}>
          chevron_right
        </span>

        {/* Date */}
        <time
          dateTime={activity.activityDate}
          className="text-xs text-[var(--text-muted)] shrink-0 w-20"
        >
          {formatDate(activity.activityDate)}
        </time>

        {/* Type badge */}
        <div className="shrink-0">
          <StatusBadge status="complete" label={activity.activityTypeName} />
        </div>

        {/* Title + description */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[var(--text-primary)] truncate">
            {activity.title}
          </p>
          {activity.description && (
            <p className="text-xs text-[var(--text-muted)] truncate">{activity.description}</p>
          )}
        </div>

        {/* Hours */}
        <span className="text-sm font-semibold text-[var(--text-primary)] shrink-0">
          {formatHours(activity.hours)} hrs
        </span>

        {/* Status indicators */}
        <div className="flex items-center gap-2 shrink-0">
          <StatusBadge
            status={hasReflection ? 'complete' : 'empty'}
            label={hasReflection ? 'Reflected' : 'No reflection'}
          />
          {hasCert && (
            <span className="material-symbols-outlined text-[16px] text-amber-500" title="Certificate attached">
              workspace_premium
            </span>
          )}
        </div>
      </button>

      {/* Expanded content */}
      {isOpen && (
        <div className="border-t border-[var(--border-subtle)] bg-[var(--surface-subtle)]">

          {/* Activity details + edit/delete */}
          <div className="px-4 pt-3 pb-2 flex flex-col sm:flex-row sm:items-start gap-3">
            <div className="flex-1 min-w-0 space-y-1">
              <p className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider">Activity</p>
              <p className="text-sm font-medium text-[var(--text-primary)]">{activity.title}</p>
              {activity.description && (
                <p className="text-xs text-[var(--text-secondary)]">{activity.description}</p>
              )}
              <p className="text-xs text-[var(--text-muted)]">
                {formatDate(activity.activityDate)} · {activity.activityTypeName} · {formatHours(activity.hours)} hrs
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { onEdit(activity); }}
                ariaLabel={`Edit ${activity.title}`}
              >
                Edit
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { void handleDelete(); }}
                ariaDisabled={deleting}
                ariaLabel={`Delete ${activity.title}`}
                className="text-red-600 hover:bg-[var(--color-error-100)]"
              >
                {deleting ? '…' : 'Delete'}
              </Button>
            </div>
          </div>

          {/* Inline edit form */}
          {isEditing && (
            <div className="px-4 pb-4">
              <ActivityForm
                activityTypes={activityTypes}
                editingActivity={activity}
                onSave={input => onSaveEdit(activity.id, input)}
                onCancel={onCancelEdit}
              />
            </div>
          )}

          {/* Certificate section */}
          <div className="px-4 pb-3">
            <p className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-2">Certificate</p>
            <div className="rounded-[var(--radius-md)] border border-dashed border-[var(--border-subtle)] bg-[var(--surface-base)] px-3 py-2 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
              <span className="material-symbols-outlined text-[18px] text-[var(--text-muted)] shrink-0">workspace_premium</span>

              {certKey && (
                <div className="flex flex-1 items-center gap-3 min-w-0">
                  <span className="text-xs text-[var(--text-secondary)] truncate">Certificate attached</span>
                  <a href={certUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-[var(--color-done-600)] hover:underline shrink-0">View</a>
                  <button type="button" onClick={() => { void handleRemoveCert(); }} className="text-xs text-red-500 hover:underline shrink-0">Remove</button>
                </div>
              )}

              {!certKey && linkedModuleId && (
                <div className="flex flex-1 items-center gap-3 min-w-0">
                  <span className="text-xs text-[var(--text-secondary)] truncate">
                    {linkedModule ? linkedModule.moduleTitle : 'Portal certificate linked'}
                  </span>
                  <Link href={`/cert/${linkedModuleId}`} className="text-xs text-[var(--color-done-600)] hover:underline shrink-0">View cert</Link>
                  <button type="button" onClick={() => { void handleLinkModule(null); }} className="text-xs text-red-500 hover:underline shrink-0">Unlink</button>
                </div>
              )}

              {!hasCert && (
                <span className="text-xs text-[var(--text-muted)] flex-1">No certificate attached</span>
              )}

              <div className="flex items-center gap-3 shrink-0 ml-auto">
                {certError && <span className="text-xs text-red-600">{certError}</span>}

                <label className="cursor-pointer">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    className="sr-only"
                    onChange={e => {
                      const file = e.target.files?.[0];
                      if (file) void handleCertUpload(file);
                    }}
                  />
                  <span role="button" className="text-xs font-medium text-[var(--color-done-600)] hover:underline" aria-busy={uploading}>
                    {uploading ? 'Uploading…' : certKey ? 'Replace' : '↑ Upload cert'}
                  </span>
                </label>

                {completedModules.length > 0 && (
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => { setShowLinkPicker(p => !p); }}
                      className="text-xs font-medium text-[var(--color-done-600)] hover:underline"
                    >
                      {linkedModuleId ? 'Change link' : '🔗 Link portal cert'}
                    </button>
                    {showLinkPicker && (
                      <div className="absolute right-0 top-6 z-20 w-64 rounded-[var(--radius-md)] border border-[var(--border-base)] bg-[var(--surface-base)] shadow-lg py-1">
                        {linkedModuleId && (
                          <button type="button" onClick={() => { void handleLinkModule(null); }} className="w-full text-left px-3 py-2 text-xs text-red-600 hover:bg-[var(--surface-subtle)]">
                            Unlink current
                          </button>
                        )}
                        {completedModules.map(m => (
                          <button
                            key={m.moduleId}
                            type="button"
                            onClick={() => { void handleLinkModule(m.moduleId); }}
                            className="w-full text-left px-3 py-2 text-xs text-[var(--text-primary)] hover:bg-[var(--surface-subtle)] truncate"
                          >
                            {m.moduleTitle}{m.moduleId === linkedModuleId && ' ✓'}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Reflection panel */}
          <div className="px-4 pb-4">
            <p className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-2">Reflection</p>
            <AIReflectionPanel
              activityId={activity.id}
              activityTitle={activity.title}
              onSaved={r => { setHasReflection(true); onReflectionSaved?.(r); }}
            />
          </div>

        </div>
      )}
    </div>
  );
}
